// The faces lying in one plane, across the room (CO-5858): what a guide
// highlighting a face adds beside it, so the faces in line with it on other
// components and products show too. Upright planes take products' exterior
// faces; a level plane takes the tops and bottoms of their solid components,
// as the alignment guide does.
//
// A drag asks every move, but its other products hold still while it lasts,
// so each plane is searched once per drag. A search measures only the
// products whose box reaches the plane: measuring a product's faces is the
// costly step, and most of the room is nowhere near a given plane.
import * as THREE from 'three';
import {axisIndex} from 'components/customer/RoomPlanner/helpers/axes';
import {flattenBox} from 'components/customer/RoomPlanner/helpers/boxes';
import type {ExteriorFace} from 'components/customer/RoomPlanner/helpers/componentGeometry';
import type {SnapPlane} from 'components/customer/RoomPlanner/helpers/snapping/strategies/faceSnap';
import type {SurfaceFacts} from 'components/customer/RoomPlanner/helpers/snapping/types';

// Faces this close to a plane (2.5 mm) lie in it.
const IN_PLANE = 0.5;

/**
 * What a product's faces are found from: its exterior faces, and its solid
 * components' boxes for tops and bottoms.
 */
export interface FaceSource {
    faces: readonly ExteriorFace[];
    solids?: readonly THREE.Box3[];
}

/**
 * @param {FaceSource} source A product's faces and solid boxes.
 * @param {SnapPlane} plane A plane.
 * @return {THREE.Box3[]} Its faces lying in the plane, each flat on it.
 */
export const facesInPlane = (
    {faces, solids = []}: FaceSource,
    {axis, position}: SnapPlane
): THREE.Box3[] => {
    const near = (at: number) => Math.abs(at - position) < IN_PLANE;
    if (axis !== 'y') {
        return faces
            .filter((face) => face.axis === axis && near(face.position))
            .map(({rect}) => rect);
    }

    return solids
        .filter(({min, max}) => near(min.y) || near(max.y))
        .map((solid) => flattenBox(solid, 1, position));
};

/**
 * Every face of a set of products lying in a plane, searched once per plane.
 */
export class PlaneFaces {
    private readonly found = new Map<string, THREE.Box3[]>();

    /**
     * @param {SurfaceFacts[]} products The products and walls to search, which hold
     * still for as long as this is kept.
     */
    constructor(private readonly products: readonly SurfaceFacts[]) {}

    /**
     * @param {SnapPlane} plane A plane.
     * @return {THREE.Box3[]} Every face of the products lying in it. Shared,
     * so read only.
     */
    in(plane: SnapPlane): THREE.Box3[] {
        const key = `${plane.axis}:${plane.position.toFixed(2)}`;
        let faces = this.found.get(key);
        if (!faces) {
            const index = axisIndex(plane.axis);
            const reaches = ({bbox}: SurfaceFacts) =>
                bbox.min.getComponent(index) - IN_PLANE < plane.position &&
                plane.position < bbox.max.getComponent(index) + IN_PLANE;
            faces = this.products.filter(reaches).flatMap((product) =>
                facesInPlane(
                    {
                        faces: plane.axis === 'y' ? [] : product.exteriorFaces,
                        solids: product.solidBBoxes,
                    },
                    plane
                )
            );
            this.found.set(key, faces);
        }

        return faces;
    }
}

// How far past an edge the outline looks to tell whether another face
// carries on beyond it. Wider than the gaps between faces flattened onto one
// plane, narrower than any face.
const BEYOND = 0.05;

// The two ways in the plane, u and v: the first and second axes that aren't
// its normal.
type Way = 'u' | 'v';

/**
 * A face as it lies in the plane: its extent each way.
 */
interface Rect {
    u: readonly [number, number];
    v: readonly [number, number];
}

/**
 * One side of a face, as it lies in the plane: running `along` one way, at
 * `at` across it, from `from` to `to`, looking `outward`, away from the
 * face.
 */
interface Side {
    along: Way;
    at: number;
    from: number;
    to: number;
    outward: 1 | -1;
}

/**
 * The outline of faces lying in one plane, drawn once around everything they
 * cover rather than once around each: a side shared by two faces side by
 * side, or lying inside another face, is left out, and pieces of one line
 * that meet are joined, so a run of faces in line is one outline.
 * @param {THREE.Box3[]} faces Faces flat on the plane.
 * @param {SnapPlane} plane The plane.
 * @return {Array<THREE.Vector3[]>} The outline's lines, as pairs of ends.
 */
export const outlineOfFaces = (
    faces: readonly THREE.Box3[],
    {axis, position}: SnapPlane
): THREE.Vector3[][] => {
    const normal = axisIndex(axis);
    const [uIndex, vIndex] = [0, 1, 2].filter((index) => index !== normal);
    const rects: Rect[] = faces.map(({min, max}) => ({
        u: [min.getComponent(uIndex), max.getComponent(uIndex)],
        v: [min.getComponent(vIndex), max.getComponent(vIndex)],
    }));
    // A rect's extent along a way, and across it.
    const spanOf = (rect: Rect, along: Way) =>
        along === 'u' ? rect.u : rect.v;
    const levelsOf = (rect: Rect, along: Way) =>
        along === 'u' ? rect.v : rect.u;
    const covered = (u: number, v: number) =>
        rects.some(
            (rect) =>
                rect.u[0] < u && u < rect.u[1] && rect.v[0] < v && v < rect.v[1]
        );
    // Whether a face carries on just beyond a side, at a point along it.
    const coveredBeyond = (along: Way, onLine: number, across: number) =>
        along === 'u' ? covered(onLine, across) : covered(across, onLine);

    // Every side of every face, cut where another face's corners fall along
    // it, keeping the pieces with nothing beyond them.
    const pieces: Side[] = [];
    rects.forEach((rect) => {
        (['u', 'v'] as const).forEach((along) => {
            const [start, end] = spanOf(rect, along);
            const [low, high] = levelsOf(rect, along);
            const cuts = [
                start,
                end,
                ...rects.flatMap((other) => spanOf(other, along)),
            ]
                .filter((cut) => start <= cut && cut <= end)
                .sort((a, b) => a - b);
            (
                [
                    [low, -1],
                    [high, 1],
                ] as const
            ).forEach(([at, outward]) => {
                cuts.slice(1).forEach((to, i) => {
                    const from = cuts.at(i) ?? start;
                    if (to - from < BEYOND) return;
                    const beyond = at + outward * BEYOND;
                    if (!coveredBeyond(along, (from + to) / 2, beyond)) {
                        pieces.push({along, at, from, to, outward});
                    }
                });
            });
        });
    });

    // Pieces on the same line, facing the same way, that meet or repeat,
    // joined into one.
    const lines = new Map<string, Side[]>();
    pieces.forEach((piece) => {
        const key = `${piece.along}:${piece.outward}:${piece.at.toFixed(2)}`;
        lines.set(key, [...(lines.get(key) ?? []), piece]);
    });
    const joined = Array.from(lines.values()).flatMap((line) =>
        line
            .sort((a, b) => a.from - b.from)
            .reduce<Side[]>((runs, piece) => {
                const last = runs.at(-1);
                if (last && piece.from <= last.to + BEYOND) {
                    last.to = Math.max(last.to, piece.to);
                } else {
                    runs.push({...piece});
                }

                return runs;
            }, [])
    );

    const point = (along: Way, onLine: number, at: number) =>
        new THREE.Vector3()
            .setComponent(normal, position)
            .setComponent(along === 'u' ? uIndex : vIndex, onLine)
            .setComponent(along === 'u' ? vIndex : uIndex, at);

    return joined.map(({along, at, from, to}) => [
        point(along, from, at),
        point(along, to, at),
    ]);
};
